// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DashboardGrid.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import { autorun, action } from 'mobx';
import UiState from 'logic/Izenda.UiState';
import GridView from 'share/components/controls/GridLayout/Izenda.GridView';
import GridItem from 'share/components/controls/GridLayout/Izenda.GridItem';
import DashboardTile from './DashboardTile/Izenda.DashboardTile';
import Dimensions from 'share/components/hoc/Izenda.Dimensions';
import DashboardGridLayoutUIStore from './Izenda.DashboardGridLayoutUIStore';
import DashboardGridConstants from 'logic/data/Izenda.DashboardGridConstants';
import { memoize, some, find, flow, filter, forEach, isNil, getOr } from 'lodash/fp';
import { ACCESS_RIGHT, PERMISSION_KEY } from 'logic/data/Izenda.AuthorizationConstants';

const newPromise = memoize(() => {
  let resolvePromise = null;
  const renderPromise = new Promise(resolve => {
    resolvePromise = resolve;
  });
  return {
    promise: renderPromise,
    resolve: resolvePromise
  };
});

@connect({ toClass: true }, Dimensions)
export default class DashboardGrid extends Component {
  static propTypes = {
    store: PropTypes.object.isRequired,
    width: PropTypes.number.isRequired
  };

  constructor(props) {
    super(props);
    this.layoutStore = null;
    const width = props.width;
    const dashboardDetail = props.store.dashboardDetail;

    this.layoutStore = new DashboardGridLayoutUIStore(width, dashboardDetail.dashboard.transparentDashboardGrid);
    this.disposeAutoRunTransparent = autorun(() => {
      this.layoutStore.setTransparent(dashboardDetail.dashboard.transparentDashboardGrid);
    });
  }

  componentWillUnmount() {
    this.disposeAutoRunTransparent();
  }

  componentWillReceiveProps(nextProps: any): void {
    if (nextProps.width !== this.props.width) {
      // sync dashboard grid width with layout.
      // As layout keeps 12 cols, the cell grid size is kept to update.
      this.layoutStore.setViewWidth(nextProps.width);
    }
  }

  shouldComponentUpdate(nextProps: any): void {
    const { id, width, newDashboard } = this.props;
    return nextProps.width !== width || nextProps.newDashboard !== newDashboard || nextProps.id !== id;
  }

  setDom = (dom: any) => {
    dom && this.layoutStore.setViewWidth(dom.clientWidth);
  };

  checkPermission = (): boolean => {
    const { props: { authorization, newDashboard, store: { dashboardDetail } } } = this;
    const isSystemAdmin = UiState.currentUser.systemAdmin;
    const isSystemLevel = isNil(UiState.tenantId);
    const accessPriority = dashboardDetail.dashboard.accessPriority;

    const hasSavePrivilege =
      newDashboard || authorization.checkPrivileges([PERMISSION_KEY.DashboardsActionsOverwriteExistingDashboard]);

    const hasSaveAsPrivilege = authorization.checkPrivileges([PERMISSION_KEY.DashboardsCanCreateNewDashboardValue]);
    var boardDetail = getOr({}, 'dashboard', dashboardDetail);

    return (
      (hasSavePrivilege &&
        accessPriority === ACCESS_RIGHT.FullAccess &&
        !(boardDetail.isGlobal && !isSystemAdmin) &&
        !(boardDetail.isGlobal && !isSystemLevel)) ||
      (hasSaveAsPrivilege && (accessPriority === ACCESS_RIGHT.FullAccess || accessPriority === ACCESS_RIGHT.SaveAs))
    );
  };

  render() {
    const { props, props: { store, store: { dashboardDetail }, printingMode, renderListeners } } = this;
    const parts = dashboardDetail.dashboard.activeDashboardParts;
    const layoutData = parts.map(toLayoutItem);

    const editDashboard = this.checkPermission();

    const gridItems = parts.map(dashboardPart => {
      const tPromise = newPromise(dashboardPart.id);
      renderListeners && renderListeners.push(tPromise.promise);
      return (
        <GridItem
          key={dashboardPart.id}
          id={dashboardPart.id}
          isNew={dashboardPart.isNew}
          isDraggable={!dashboardPart.isFullSize && editDashboard}
          isResizable={!dashboardPart.isFullSize && editDashboard}
          handle=".draggableBox"
          printingMode={printingMode}
          cancel=".btn, input, a[href]"
        >
          <DashboardTile
            editDashboard={editDashboard}
            {...props}
            store={store}
            dashboardPart={dashboardPart}
            viewMode={!!props.viewMode}
            finishedRender={tPromise.resolve}
          />
        </GridItem>
      );
    });

    return (
      <section className={props.className} style={props.style} ref={this.setDom}>
        <GridView
          className="izenda-DashboardGrid"
          layoutStore={this.layoutStore}
          layoutData={layoutData}
          editable={!props.viewMode && !UiState.isMobile}
          allowAddInline={!props.viewMode && !UiState.isMobile && editDashboard}
          onAddItem={this.onAddItem}
          printingMode={printingMode}
          onUpdateItemPositions={this.onUpdateItemPositions}
        >
          {gridItems}
        </GridView>
      </section>
    );
  }

  /**
   * Add new dashboard part
   * @param  {Object} position Position of new dashboard part
   */
  onAddItem = (position: any): void => {
    position = position || { left: 0, top: 0 };
    const dashboardDetail = this.props.store.dashboardDetail;
    dashboardDetail.addDashboardTile(position.left, position.top);
  };
  /**
   * Update new dashboard tile positions
   * @param  {Object} newItemPositions           New positions
   */
  @action
  onUpdateItemPositions = (newItemPositions: Array<any>): void => {
    const needPartUpdate = tile => some({ id: tile.id }, newItemPositions);

    const setOriginalPosition = tile => {
      if (!UiState.isMobile) {
        tile.originalPosition = null;
      } else {
        isNil(tile.originalPosition) && tile.setOriginalPosition({
          positionX: tile.positionX,
          positionY: tile.positionY,
          width: tile.width,
          height: tile.height
        });
      }
    };

    const setPosition = tile => {
      const newPos = find({ id: tile.id }, newItemPositions);
      tile.setPosition({
        positionX: newPos.left,
        positionY: newPos.top,
        width: newPos.width,
        height: newPos.height
      });
    };

    const dashboardTiles = this.props.store.dashboardDetail.dashboard.dashboardParts;

     forEach(setOriginalPosition)(dashboardTiles);

    flow(filter(needPartUpdate), forEach(setPosition))(dashboardTiles);
  };
}

/**
 * 
 * @param {object} item 
 * @param {number} index 
 * @return {object} item data
 */
function toLayoutItem(item: any, index: number): any {
  if (UiState.isMobile) {
    return {
      id: item.id,
      x: 0,
      y: index,
      width: DashboardGridConstants.DEFAULT_TILE_WIDTH,
      height: item.height,
      isFullSize: item.isFullSize
    };
  } else {
    const originalPosition = getOr(null, 'originalPosition', item);
    return !isNil(originalPosition) ? {
      id: item.id,
      x: originalPosition.positionX,
      y: originalPosition.positionY,
      width: originalPosition.width,
      height: originalPosition.height,
      isFullSize: item.isFullSize
    } : {
      id: item.id,
      x: item.positionX,
      y: item.positionY,
      width: item.width,
      height: item.height,
      isFullSize: item.isFullSize
    };
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Dashboard/DashboardDesigner/DashboardGrid/Izenda.DashboardGrid.js